import { useEffect, useState } from "react";
import { History, MessageCircle, Search, Send, ShieldAlert, Unlink, Wifi } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useRouters } from "../context/RouterContext";
import PinConfirm from "../components/PinConfirm";
import { ChatWindow } from "../components/ChatWindow";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { RouterCombobox } from "../components/RouterCombobox";
import { SisaVoucher } from "../components/SisaVoucher";

const fmtBytes = (b) => {
  if (!b) return "0 MB";
  if (b >= 1073741824) return `${(b / 1073741824).toFixed(2)} GB`;
  return `${(b / 1048576).toFixed(1)} MB`;
};

export default function ResetVoucher() {
  const { user } = useAuth();
  const isAdmin = ["admin", "admin_fasnet"].includes(user?.role);
  const { selected, router, routers, setSelected } = useRouters();
  const [code, setCode] = useState("");
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lepasBusy, setLepasBusy] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);
  const [reqNote, setReqNote] = useState("");
  const [reqBusy, setReqBusy] = useState(false);
  const [reqs, setReqs] = useState([]);
  const [logs, setLogs] = useState([]);
  const [chatOpen, setChatOpen] = useState(false);

  const loadLogs = () => api.get("/mac-unbind/logs").then((r) => setLogs(r.data)).catch(() => {});
  useEffect(() => { loadLogs(); }, []);
  useEffect(() => { setDetail(null); }, [selected]);
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get("router")) setSelected(q.get("router"));
    if (q.get("code")) setCode(q.get("code"));
  }, []); // eslint-disable-line

  const cari = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setLoading(true);
    setDetail(null);
    try {
      const { data } = await api.get(`/routers/${selected}/vouchers/${encodeURIComponent(code.trim())}/detail`);
      setDetail(data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  const lepas = async () => {
    setLepasBusy(true);
    try {
      const { data } = await api.post(`/routers/${selected}/vouchers/${encodeURIComponent(detail.name)}/lepas-mac`);
      toast.success(`MAC ${data.old_mac || "-"} dilepas dari ${detail.name}${data.kicked ? ` • ${data.kicked} sesi aktif diputus` : ""}. Pelanggan bisa login ulang.`);
      setDetail({ ...detail, mac_address: "", active_sessions: 0, reset_count: (detail.reset_count || 0) + 1 });
      loadLogs();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLepasBusy(false);
    }
  };

  const ajukan = async () => {
    setReqBusy(true);
    try {
      await api.post(`/routers/${selected}/vouchers/${encodeURIComponent(detail.name)}/ajukan-reset`, { note: reqNote });
      toast.success("Pengajuan reset terkirim ke Admin — Anda akan dapat pesan di chat setelah disetujui.");
      setReqNote("");
      setDetail({ ...detail, pending_request: { by_name: user?.name || user?.email, created_at: new Date().toISOString() } });
    } catch (err) { toast.error(errMsg(err)); } finally { setReqBusy(false); }
  };

  const loadReqs = () => { if (isAdmin) api.get("/reset-requests").then((r) => setReqs(r.data)).catch(() => {}); };
  useEffect(() => { loadReqs(); }, [isAdmin]); // eslint-disable-line
  const setujui = async (r) => {
    if (!window.confirm(`Setujui reset voucher ${r.voucher} (${r.router_name}) untuk ${r.by_name}? MAC akan dilepas & sesi diputus.`)) return;
    try { const { data } = await api.post(`/reset-requests/${r.id}/setujui`); toast.success(`Disetujui — MAC ${data.old_mac || "-"} dilepas${data.kicked ? `, ${data.kicked} sesi diputus` : ""}`); loadReqs(); loadLogs(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const tolak = async (r) => {
    const note = window.prompt(`Alasan menolak reset ${r.voucher}? (opsional)`) ?? null;
    if (note === null) return;
    try { await api.post(`/reset-requests/${r.id}/tolak`, { note }); toast.success("Pengajuan ditolak"); loadReqs(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const routerPicker = (
    <Card className="bg-card border-border shadow-sm" data-testid="reset-router-pick-card">
      <CardContent className="p-5 space-y-1.5">
        <Label>Cabang / Router</Label>
        <RouterCombobox routers={routers} value={selected || ""} onChange={setSelected} placeholder="Pilih cabang/router" testid="select-reset-router" className="h-10 text-sm" />
      </CardContent>
    </Card>
  );

  if (!selected) {
    return (
      <div className="space-y-6 max-w-2xl" data-testid="reset-voucher-no-router">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Reset Voucher</h1>
          <p className="text-sm text-muted-foreground mt-1">Pilih cabang/router tempat voucher berada terlebih dahulu.</p>
        </div>
        {routerPicker}

        {!isAdmin && (
          <div className="rounded-xl border-2 border-rose-500/40 bg-rose-500/10 p-4 space-y-1.5" data-testid="reset-antifraud-warning">
            <div className="flex items-center gap-2 text-rose-700 dark:text-rose-400 font-bold text-sm uppercase tracking-wide">
              <ShieldAlert className="w-5 h-5 shrink-0" /> Peringatan Tegas untuk Mitra
            </div>
            <p className="text-sm text-rose-900 dark:text-rose-100 leading-relaxed">
              Setiap reset voucher tercatat permanen oleh sistem: kode voucher, perangkat (MAC), waktu, dan identitas akun Anda — serta otomatis dilaporkan ke Admin. Segala bentuk kecurangan sistem, termasuk mereset voucher yang sudah habis masa berlakunya, mereset berulang untuk dipakai banyak perangkat, atau memperjualbelikan hasil reset, <span className="font-bold text-rose-700 dark:text-rose-300">akan berakibat PEMBLOKIRAN AKUN secara permanen tanpa peringatan lebih lanjut.</span>
            </p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl" data-testid="reset-voucher-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Reset Voucher</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {isAdmin ? <>Konsumen tidak bisa login karena voucher terkunci di perangkat lain? Ketik kode voucher — sistem menghapus MAC Address yang menempel di MikroTik <span className="font-medium text-foreground">({router?.name})</span> agar voucher bisa login di perangkat baru.</>
            : <>Ketik kode voucher lalu <b>Cek Kode</b>. Jika masih berlaku dan terkunci di HP lain, tekan <b>Reset Kode Ini</b>. Jika sudah expired, Anda bisa langsung chat admin.</>}
        </p>
      </div>

      {routerPicker}

      {isAdmin && reqs.length > 0 && (
        <Card className="bg-card border-amber-500/40 shadow-sm" data-testid="reset-requests-card">
          <CardHeader><CardTitle className="text-base font-heading flex items-center gap-2"><Send className="w-4 h-4 text-amber-500" /> Pengajuan Reset dari Agen ({reqs.length})</CardTitle></CardHeader>
          <CardContent className="p-0 divide-y divide-border">
            {reqs.map((r) => (
              <div key={r.id} className="px-4 py-3 flex flex-wrap items-center gap-3 text-sm" data-testid={`reset-req-${r.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-medium">Voucher <span className="font-mono">{r.voucher}</span> — {r.router_name} <span className="text-xs text-muted-foreground">(sudah {r.reset_count}x reset)</span></div>
                  <div className="text-xs text-muted-foreground">{r.by_agent_code} {r.by_name} • {fmtDateTime(r.created_at)}{r.note ? ` • ${r.note}` : ""}</div>
                </div>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setujui(r)} data-testid={`reset-req-approve-${r.id}`}><Unlink className="w-3.5 h-3.5 mr-1" /> Setujui & Lepas MAC</Button>
                <Button size="sm" variant="outline" className="text-rose-400 border-rose-500/40" onClick={() => tolak(r)} data-testid={`reset-req-reject-${r.id}`}>Tolak</Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {!isAdmin && (
        <div className="rounded-xl border-2 border-rose-500/40 bg-rose-500/10 p-4 space-y-1.5" data-testid="reset-antifraud-warning">
          <div className="flex items-center gap-2 text-rose-700 dark:text-rose-400 font-bold text-sm uppercase tracking-wide">
            <ShieldAlert className="w-5 h-5 shrink-0" /> Peringatan Tegas untuk Mitra
          </div>
          <p className="text-sm text-rose-900 dark:text-rose-100 leading-relaxed">
            Setiap reset voucher tercatat permanen oleh sistem: kode voucher, perangkat (MAC), waktu, dan identitas akun Anda — serta otomatis dilaporkan ke Admin. Segala bentuk kecurangan sistem, termasuk mereset voucher yang sudah habis masa berlakunya, mereset berulang untuk dipakai banyak perangkat, atau memperjualbelikan hasil reset, <span className="font-bold text-rose-700 dark:text-rose-300">akan berakibat PEMBLOKIRAN AKUN secara permanen tanpa peringatan lebih lanjut.</span>
          </p>
        </div>
      )}

      <Card className="bg-card border-border shadow-sm">
        <CardContent className="p-5">
          <form onSubmit={cari} className="flex gap-2 items-end">
            <div className="space-y-1.5 flex-1">
              <Label>Kode Voucher</Label>
              <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="contoh: AB12CD" className="font-mono" data-testid="input-reset-kode" />
            </div>
            <Button type="submit" disabled={loading} data-testid="btn-reset-cari">
              <Search className="w-4 h-4 mr-1.5" /> {loading ? "Mengecek..." : "Cek Kode"}
            </Button>
          </form>
        </CardContent>
      </Card>

      {detail && !isAdmin && (
        <Card className={`shadow-sm border-2 ${detail.habis ? "border-rose-500/50" : "border-emerald-500/50"}`} data-testid="reset-simple-card">
          <CardContent className="p-5 space-y-4 text-center">
            <div className="font-mono text-2xl font-bold tracking-widest" data-testid="reset-simple-code">{detail.name}</div>
            {detail.habis ? (
              <>
                <div className="rounded-xl bg-rose-500/10 text-rose-600 dark:text-rose-300 p-4 space-y-1" data-testid="reset-simple-expired">
                  <div className="font-bold text-base">Voucher sudah expired / habis</div>
                  <div className="text-xs">{detail.habis_reason || "Masa berlaku voucher ini telah berakhir, sehingga tidak dapat direset."}</div>
                  {detail.used_at && <div className="text-[11px] opacity-80">Login pertama: {fmtDateTime(detail.used_at)} • Profile {detail.profile}</div>}
                </div>
                <p className="text-sm text-muted-foreground">Jika Anda merasa voucher ini masih berlaku, chat admin di sini — kode voucher otomatis disertakan.</p>
                <Button className="w-full h-11 bg-sky-600 hover:bg-sky-500 text-white" onClick={() => setChatOpen(true)} data-testid="btn-reset-chat-admin">
                  <MessageCircle className="w-4 h-4 mr-1.5" /> Chat Admin tentang kode ini
                </Button>
              </>
            ) : (
              <>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div className="rounded-lg bg-accent/40 p-2"><div className="text-muted-foreground">Profile</div><div className="font-mono font-bold">{detail.profile || "-"}</div></div>
                  <div className="rounded-lg bg-accent/40 p-2"><div className="text-muted-foreground">Sisa</div><div className="font-mono font-bold">{detail.sisa !== null && detail.sisa !== undefined ? <SisaVoucher s={detail.sisa} disabled={detail.disabled} /> : (detail.used_at ? "-" : "Belum login")}</div></div>
                  <div className="rounded-lg bg-accent/40 p-2"><div className="text-muted-foreground">Perangkat</div><div className="font-mono font-bold">{detail.mac_address ? "Terkunci" : "Bebas"}</div></div>
                </div>
                {detail.mac_address ? (
                  <>
                    <div className="text-xs text-muted-foreground">Voucher masih berlaku dan terkunci di perangkat <span className="font-mono">{detail.mac_address}</span>.</div>
                    {detail.can_reset ? (
                      <Button className="w-full h-12 text-base bg-emerald-600 hover:bg-emerald-500 text-white" onClick={() => setPinOpen(true)} disabled={lepasBusy} data-testid="btn-reset-kode-ini">
                        <Unlink className="w-5 h-5 mr-2" /> {lepasBusy ? "Mereset..." : "Reset Kode Ini"}
                      </Button>
                    ) : detail.pending_request ? (
                      <div className="rounded-lg border border-sky-500/40 bg-sky-500/10 p-2.5 text-xs text-sky-700 dark:text-sky-300" data-testid="reset-request-pending">⏳ Pengajuan reset menunggu persetujuan Admin ({fmtDateTime(detail.pending_request.created_at)}).</div>
                    ) : (
                      <div className="space-y-2" data-testid="reset-request-box">
                        <p className="text-xs text-amber-600 dark:text-amber-300 font-medium">{detail.block_reason}</p>
                        <Input placeholder="Alasan (opsional) — mis. konsumen ganti HP" value={reqNote} onChange={(e) => setReqNote(e.target.value)} className="h-9" data-testid="reset-request-note" />
                        <Button className="w-full bg-amber-500 hover:bg-amber-600 text-black" disabled={reqBusy} onClick={ajukan} data-testid="btn-reset-ajukan"><Send className="w-4 h-4 mr-1.5" /> {reqBusy ? "Mengirim..." : "Ajukan Reset ke Admin"}</Button>
                      </div>
                    )}
                    <p className="text-[11px] text-muted-foreground" data-testid="reset-quota-info">Reset kode ini: {detail.reset_count}/{detail.reset_self_limit}x • hari ini {detail.reset_today}/{detail.reset_daily_limit}</p>
                  </>
                ) : (
                  <div className="rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-300 p-4 text-sm" data-testid="reset-mac-free-box">
                    <div className="font-bold">Voucher masih berlaku & tidak terkunci perangkat</div>
                    <div className="text-xs mt-1">Tidak perlu direset — konsumen bisa langsung login di perangkat mana pun. Jika tetap tidak bisa login, chat admin.</div>
                    <Button variant="outline" size="sm" className="mt-2" onClick={() => setChatOpen(true)} data-testid="btn-reset-chat-admin"><MessageCircle className="w-4 h-4 mr-1.5" /> Chat Admin</Button>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {detail && isAdmin && (
        <Card className="bg-card border-border shadow-sm" data-testid="reset-detail-card">
          <CardHeader><CardTitle className="text-base font-heading flex items-center gap-2"><Wifi className="w-4 h-4 text-sky-500" /> Data Voucher: <span className="font-mono">{detail.name}</span></CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <div className="text-muted-foreground">Profile</div><div className="font-mono text-right" data-testid="reset-detail-profile">{detail.profile || "-"}</div>
              <div className="text-muted-foreground">Status</div>
              <div className="text-right" data-testid="reset-detail-status">
                <span className={`px-2 py-0.5 rounded-full text-xs font-mono border ${detail.disabled ? "bg-rose-500/10 text-rose-400 border-rose-500/20" : "bg-emerald-500/10 text-emerald-500 border-emerald-500/20"}`}>
                  {detail.disabled ? "Disabled" : "Enable"}
                </span>
                {detail.habis && <span className="ml-1 px-2 py-0.5 rounded-full text-xs font-mono border bg-rose-500/10 text-rose-400 border-rose-500/20" data-testid="reset-badge-habis">Habis</span>}
              </div>
              <div className="text-muted-foreground">Masa aktif</div><div className="font-mono text-right" data-testid="reset-detail-uptime">{detail.uptime || "0s"}{detail.limit_uptime ? ` / ${detail.limit_uptime}` : ""}</div>
              <div className="text-muted-foreground">Pemakaian data</div><div className="font-mono text-right">{fmtBytes(detail.bytes_in + detail.bytes_out)}{detail.limit_bytes_total ? ` / ${fmtBytes(detail.limit_bytes_total)}` : ""}</div>
              <div className="text-muted-foreground">Tanggal Login</div><div className="font-mono text-right" data-testid="reset-detail-used-at">{detail.used_at ? fmtDateTime(detail.used_at) : "Belum pernah login"}</div>
              {detail.sisa !== null && detail.sisa !== undefined && <><div className="text-muted-foreground">Sisa</div><div className="font-mono text-right" data-testid="reset-detail-sisa"><SisaVoucher s={detail.sisa} disabled={detail.disabled} /></div></>}
              <div className="text-muted-foreground">Sesi aktif</div><div className="font-mono text-right" data-testid="reset-detail-sessions">{detail.active_sessions}</div>
            </div>

            {detail.mac_address ? (
              <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-4 space-y-3" data-testid="reset-mac-bound-box">
                <div className="flex items-center gap-2 text-sm">
                  <ShieldAlert className="w-4 h-4 text-rose-500 shrink-0" />
                  <span>Voucher ini <span className="font-semibold">terkunci</span> ke perangkat dengan MAC:</span>
                </div>
                <div className="font-mono font-bold text-lg text-rose-500 text-center tracking-wider" data-testid="reset-detail-mac">{detail.mac_address}</div>
                {detail.can_request && !detail.pending_request && (
                  <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-2.5 space-y-2" data-testid="reset-request-box">
                    <p className="text-[11px] text-amber-700 dark:text-amber-300 font-medium">{detail.block_reason}</p>
                    <Input placeholder="Alasan (opsional) — mis. konsumen ganti HP" value={reqNote} onChange={(e) => setReqNote(e.target.value)} className="h-9" data-testid="reset-request-note" />
                    <Button className="w-full bg-amber-500 hover:bg-amber-600 text-black" disabled={reqBusy} onClick={ajukan} data-testid="btn-reset-ajukan">
                      <Send className="w-4 h-4 mr-1.5" /> {reqBusy ? "Mengirim..." : "Ajukan Reset ke Admin"}
                    </Button>
                  </div>
                )}
                {detail.pending_request && (
                  <div className="rounded-lg border border-sky-500/40 bg-sky-500/10 p-2.5 text-[11px] text-sky-700 dark:text-sky-300 font-medium" data-testid="reset-request-pending">
                    ⏳ Pengajuan reset oleh {detail.pending_request.by_name} ({fmtDateTime(detail.pending_request.created_at)}) menunggu persetujuan Admin.
                  </div>
                )}
                <Button variant="destructive" className="w-full" onClick={() => setPinOpen(true)} disabled={lepasBusy || !detail.can_reset} data-testid="btn-reset-lepas-mac">
                  <Unlink className="w-4 h-4 mr-1.5" /> {lepasBusy ? "Melepas..." : "Hapus MAC Address"}
                </Button>
                {!detail.can_reset && !detail.can_request && <p className="text-[11px] text-rose-400 text-center font-medium" data-testid="reset-block-reason">{detail.block_reason}</p>}
                <p className="text-[11px] text-muted-foreground text-center" data-testid="reset-quota-info">Reset voucher ini: {detail.reset_count}/{detail.reset_self_limit}x{detail.is_admin ? "" : ` (sisa ${Math.max(0, detail.reset_self_limit - detail.reset_count)}x selama belum expired)`} • Reset Anda hari ini: {detail.reset_today}/{detail.reset_daily_limit}{detail.is_admin ? " (admin tanpa batas)" : ""}</p>
                <p className="text-[11px] text-muted-foreground text-center">MAC akan dilepas & sesi aktif diputus — konsumen bisa login ulang di perangkat baru.</p>
              </div>
            ) : (
              <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4 text-center text-sm text-emerald-600" data-testid="reset-mac-free-box">
                Tidak ada MAC Address yang menempel — voucher bebas dipakai di perangkat manapun.
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card className="bg-card border-border shadow-sm" data-testid="reset-logs-card">
        <CardHeader><CardTitle className="text-base font-heading flex items-center gap-2"><History className="w-4 h-4 text-sky-500" /> Riwayat Reset Voucher</CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-border max-h-64 overflow-y-auto" data-testid="reset-logs">
            {logs.map((l) => (
              <div key={l.id} className="px-4 py-2.5 text-xs flex items-center gap-3" data-testid={`reset-log-${l.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-medium">Voucher <span className="font-mono">{l.voucher}</span> — MAC <span className="font-mono">{l.old_mac || "-"}</span> dilepas</div>
                  <div className="text-muted-foreground font-mono">{fmtDateTime(l.created_at)} • {l.router_name || ""}{l.kicked ? ` • ${l.kicked} sesi diputus` : ""}</div>
                </div>
              </div>
            ))}
            {!logs.length && <div className="px-4 py-8 text-center text-xs text-muted-foreground">Belum ada riwayat reset voucher</div>}
          </div>
        </CardContent>
      </Card>

      <PinConfirm
        open={pinOpen}
        onOpenChange={setPinOpen}
        title={`Reset voucher ${detail?.name}?`}
        description={`MAC ${detail?.mac_address || ""} akan dilepas dan sesi aktif voucher diputus. Konsumen bisa login ulang di perangkat baru.`}
        confirmLabel={isAdmin ? "Ya, Hapus MAC Address" : "Ya, Reset Kode Ini"}
        destructive
        onConfirm={lepas}
        testid="pin-reset-voucher"
      />
      {chatOpen && detail && (
        <ChatWindow title={<>Chat Admin — <span className="font-mono">{detail.name}</span></>} channel={`agent:${user?.id}`} testid="reset-chat-admin"
          initialText={`Halo Admin, mohon cek voucher ${detail.name} (${router?.name}, profile ${detail.profile || "-"}). Sistem menyatakan ${detail.habis ? "sudah expired/habis" : "masih berlaku"}${detail.used_at ? `, login pertama ${fmtDateTime(detail.used_at)}` : ""}. Konsumen melaporkan voucher masih berlaku tapi tidak bisa login. `}
          onClose={() => setChatOpen(false)} />
      )}
    </div>
  );
}
